import {
  Button,
  CheckboxField,
  Grid,
  Label,
  Loader,
  SelectField,
  TextField,
} from "@aws-amplify/ui-react";
import { CCard } from "@coreui/react";
import { useContext, useEffect, useRef, useState } from "react";
import LocalizedStrings from "react-localization";
import toast from "react-hot-toast";
import { API, graphqlOperation } from "aws-amplify";
import SignatureCanvas from "react-signature-canvas";

import {
  activitiesByMaintenanceRoutineID,
  executedServiceOrdersByServiceOrderID,
  getExecutedServiceOrder,
  getMaintenanceRoutine,
} from "graphql/queries";
import { OrderContext } from "components/serviceOrders/context/OrderContext";
import { localGetServiceOrder } from "libs/idb/service-orders-db";
import { serviceOrderStatus } from "config/constants";
import { globalStrings } from "config/translations/es";

const ActivitiesInfo = ({
  maintenanceRoutineID,
  equipmentID,
  onChangeActivity,
}) => {
  const { serviceOrder, viewType } = useContext(OrderContext);
  const [activities, setActivities] = useState({
    data: [],
    loading: false,
  });
  const sigCanvas = useRef(null);

  const onChange = (activityId, activityValue) => {
    setActivities((prev) => {
      const listUpdated = [...prev.data];
      const index = listUpdated.findIndex((it) => it.id === activityId);

      listUpdated[index] = {
        ...listUpdated[index],
        value: activityValue,
        changed: true,
      };

      return { ...prev, data: listUpdated };
    });
    if (onChangeActivity) onChangeActivity(activityId, activityValue);
  };

  const onEndSign = (activityId) => {
    const dataUri = sigCanvas.current.toDataURL("image/png");
    onChange(activityId, dataUri);
  };

  const onClearSign = (activityId) => {
    if (sigCanvas?.current) sigCanvas.current.clear();
    onChange(activityId, "");
  };

  const getExecutedActivities = async (id, eqId) => {
    try {
      setActivities({ data: [], loading: true });

      let activitiesToRender = [];
      const localData = await localGetServiceOrder(serviceOrder.id);

      if (localData) {
        const executionFound = localData?.execution ?? {};
        const activitiesOrder =
          localData?.routines?.find((r) => r.id === id)?.activitiesOrder || [];
        const activitiesFound =
          localData?.activities?.filter((a) => a.maintenanceRoutineID === id) ??
          [];

        if (eqId)
          activitiesToRender =
            executionFound.executedRoutines?.find(
              (r) => r.routineID === id && r.equipmentID === eqId
            )?.activities ?? [];
        else
          activitiesToRender =
            executionFound.executedRoutines?.find(
              (r) => r.routineID === id && !r.equipmentID
            )?.activities ?? [];

        const dataUpdated = activitiesFound
          .map((act) => ({
            ...act,
            value:
              activitiesToRender?.find(
                (actData) => actData.activityID === act.id
              )?.activityValue || act.defaultValue,
          }))
          .sort(
            (a, b) =>
              activitiesOrder.indexOf(a.id) - activitiesOrder.indexOf(b.id)
          );
        setActivities((prev) => ({
          ...prev,
          data: dataUpdated,
        }));
        if (onChangeActivity)
          dataUpdated.forEach((act) => onChangeActivity(act.id, act.value));

        return;
      }

      const executedServiceOrdersData = await API.graphql(
        graphqlOperation(executedServiceOrdersByServiceOrderID, {
          serviceOrderID: serviceOrder.id,
          limit: 1,
          filter: {
            deletedAt: { attributeExists: false },
          },
        })
      );
      const executedServiceOrderItem =
        executedServiceOrdersData.data.executedServiceOrdersByServiceOrderID
          .items[0];

      if (executedServiceOrderItem) {
        const executedServiceOrderData = await API.graphql(
          graphqlOperation(getExecutedServiceOrder, {
            id: executedServiceOrderItem.id,
          })
        );
        const executedServiceOrder =
          executedServiceOrderData.data.getExecutedServiceOrder;

        if (eqId)
          activitiesToRender =
            executedServiceOrder.executedRoutines?.find(
              (r) => r.routineID === id && r.equipmentID === eqId
            )?.activities ?? [];
        else
          activitiesToRender =
            executedServiceOrder.executedRoutines?.find(
              (r) => r.routineID === id && !r.equipmentID
            )?.activities ?? [];
      }

      const activitiesData = await API.graphql(
        graphqlOperation(activitiesByMaintenanceRoutineID, {
          maintenanceRoutineID: id,
          filter: {
            deletedAt: { attributeExists: false },
          },
        })
      );
      const activitiesDataItems =
        activitiesData.data.activitiesByMaintenanceRoutineID.items;

      let dataUpdated = [];
      const maintenanceRoutineData = await API.graphql(
        graphqlOperation(getMaintenanceRoutine, { id })
      );
      const activitiesOrder =
        maintenanceRoutineData.data.getMaintenanceRoutine?.activitiesOrder ||
        [];

      if (
        serviceOrder.status === serviceOrderStatus.executed &&
        activitiesToRender?.length > 0
      ) {
        dataUpdated = activitiesToRender
          .map((act) => ({
            id: act.activityID,
            name:
              act.activityName ||
              activitiesDataItems?.find(
                (actDataIt) => actDataIt.id === act.activityID
              )?.name ||
              "",
            type:
              act.activityType ||
              activitiesDataItems?.find(
                (actDataIt) => actDataIt.id === act.activityID
              )?.type ||
              "",
            value: act.activityValue,
          }))
          .sort(
            (a, b) =>
              activitiesOrder.indexOf(a.id) - activitiesOrder.indexOf(b.id)
          );
      } else if (activitiesDataItems?.length > 0) {
        dataUpdated = activitiesDataItems
          .map((act) => ({
            ...act,
            value:
              activitiesToRender?.find(
                (actData) => actData.activityID === act.id
              )?.activityValue || act.defaultValue,
          }))
          .sort(
            (a, b) =>
              activitiesOrder.indexOf(a.id) - activitiesOrder.indexOf(b.id)
          );
      }
      setActivities((prev) => ({
        ...prev,
        data: dataUpdated,
      }));
      if (onChangeActivity && dataUpdated?.length > 0)
        dataUpdated.forEach((act) => onChangeActivity(act.id, act.value));
    } catch (err) {
      toast.error(strings.errors.fetchActivities);
    } finally {
      setActivities((prev) => ({ ...prev, loading: false }));
    }
  };

  useEffect(() => {
    if (maintenanceRoutineID)
      getExecutedActivities(maintenanceRoutineID, equipmentID);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maintenanceRoutineID, equipmentID]);

  return (
    <Grid gap="5px">
      <Label>{strings.activities}</Label>
      {activities.loading ? (
        <Loader />
      ) : (
        <Grid gap="10px">
          {activities.data.length > 0
            ? activities.data.map((activity) => (
                <CCard
                  className="avoidBreak"
                  borderRadius="medium"
                  variation="outlined"
                  style={{ height: "100%" }}
                >
                  <Grid className="p-3" gap="25px">
                    <Grid>
                      {viewType === "export" ? (
                        <>
                          {activity.type === "sign" ? (
                            <Grid>
                              <Label style={{ overflow: "auto" }}>
                                {activity.name}
                              </Label>
                              {activity.value ? (
                                <img
                                  width="100%"
                                  src={activity.value}
                                  alt="activityValue"
                                />
                              ) : (
                                ""
                              )}
                            </Grid>
                          ) : activity.type === "check" ? (
                            <Grid>
                              <CheckboxField
                                name="value"
                                disabled
                                label={activity.name}
                                checked={activity.value === "true"}
                              ></CheckboxField>
                            </Grid>
                          ) : (
                            <Grid>
                              <Label style={{ overflow: "auto" }}>
                                {activity.name}
                              </Label>
                              <span className="text-muted">
                                {activity.value}
                              </span>
                            </Grid>
                          )}
                        </>
                      ) : (
                        <>
                          {activity.type === "list" ? (
                            <SelectField
                              name="value"
                              value={activity.value}
                              label={
                                <Label style={{ overflow: "auto" }}>
                                  {activity.name}
                                </Label>
                              }
                              onChange={(e) =>
                                onChange(activity.id, e.target.value)
                              }
                            >
                              <option value="">
                                {globalStrings.selectOption}
                              </option>
                              {JSON.parse(activity.values)?.map((paramVal) => (
                                <option value={paramVal.value}>
                                  {paramVal.name}
                                </option>
                              ))}
                            </SelectField>
                          ) : activity.type === "sign" ? (
                            <Grid gap="15px">
                              <Label style={{ overflow: "auto" }}>
                                {activity.name}
                              </Label>
                              {activity.value && !activity.changed ? (
                                <img
                                  width="100%"
                                  className="amplify-input"
                                  src={activity.value}
                                  alt="activityValue"
                                />
                              ) : (
                                <SignatureCanvas
                                  ref={sigCanvas}
                                  canvasProps={{
                                    className: "amplify-input p-0",
                                  }}
                                  onEnd={() => onEndSign(activity.id)}
                                />
                              )}
                              <Button
                                size="small"
                                style={{
                                  float: "right",
                                  justifySelf: "flex-end",
                                }}
                                children={strings.clean}
                                onClick={() => onClearSign(activity.id)}
                              />
                            </Grid>
                          ) : activity.type === "check" ? (
                            <CheckboxField
                              name="value"
                              label={activity.name}
                              checked={activity.value === "true"}
                              onChange={(e) =>
                                onChange(
                                  activity.id,
                                  e.target.checked.toString()
                                )
                              }
                            ></CheckboxField>
                          ) : (
                            <TextField
                              name="value"
                              label={
                                <Label style={{ overflow: "auto" }}>
                                  {activity.name}
                                </Label>
                              }
                              value={activity.value}
                              onChange={(e) =>
                                onChange(activity.id, e.target.value)
                              }
                              type={activity.type ?? "text"}
                            ></TextField>
                          )}
                        </>
                      )}
                    </Grid>
                  </Grid>
                </CCard>
              ))
            : strings.noActivities}
        </Grid>
      )}
    </Grid>
  );
};

const strings = new LocalizedStrings({
  es: {
    activities: "Actividades",
    name: "Nombre",
    value: "Valor",
    clean: "Limpiar",
    errors: {
      fetchActivities: "Error al consultar las actividades",
    },
    noActivities: "Sin actividades...",
  },
});

export default ActivitiesInfo;
